/**
 * The application's email address, as distinct from Better Auth's.
 *
 * `user.email` is a Better Auth identity column: required, unique, and for a
 * phone-only signup filled with a random value at a non-routable `.invalid`
 * domain. It is not a mailbox. Reading it to display, search, export, prefill or
 * mail is the bug this module exists to prevent — it would show a person a
 * `.invalid` string, or mail a ticket into a black hole.
 *
 * `user.contactEmail` is the mailbox. It is nullable, so "no email" is a value
 * the type checker carries rather than a condition somebody has to remember to
 * test, and it is only meaningful once `contactEmailVerifiedAt` is set.
 *
 * The one intentional exception to "never read `user.email`" is Better Auth
 * itself and the auth-email allowlist in
 * `src/lib/identity/auth-email-allowlist.test.ts`.
 */

import { looksLikePhoneNumber } from "./display-name";

/**
 * Reserved domain for synthetic auth emails.
 *
 * `.invalid` is reserved by RFC 2606 precisely so that it can never resolve, so
 * a leaked address cannot accidentally deliver anywhere. The local part is a
 * random UUID, never derived from the phone number: a derived address would
 * survive the number being changed or removed, would collide when a number is
 * recycled, and would leak the number to anything that displayed it.
 */
export const AUTH_PLACEHOLDER_EMAIL_DOMAIN = "phone.hiive.invalid";

export const APPLE_PRIVATE_RELAY_SUFFIX = "@privaterelay.appleid.com";

/** Mint a fresh non-routable auth email for a phone-only signup. */
export function createPlaceholderAuthEmail(): string {
  return `u-${crypto.randomUUID()}@${AUTH_PLACEHOLDER_EMAIL_DOMAIN}`;
}

/**
 * Is this a synthetic auth identifier rather than a mailbox? Checks the whole
 * `.invalid` TLD, not just our own domain, so a value written by an older
 * revision or an import is still recognised.
 */
export function isPlaceholderAuthEmail(
  value: string | null | undefined,
): boolean {
  if (!value) return false;
  return value.trim().toLowerCase().endsWith(".invalid");
}

export function isApplePrivateRelayEmail(
  value: string | null | undefined,
): boolean {
  if (!value) return false;
  return value.trim().toLowerCase().endsWith(APPLE_PRIVATE_RELAY_SUFFIX);
}

/**
 * Normalize for storage and comparison: trim and lowercase, nothing more.
 *
 * Deliberately NOT doing Gmail-style dot/plus folding. Two addresses that fold
 * to the same mailbox at one provider do not at another, so folding would make
 * us reject a legitimate address as "already taken" by someone else.
 */
export function normalizeContactEmail(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

export type ContactEmailRejection =
  | "empty"
  | "malformed"
  | "too_long"
  | "not_routable";

export type ContactEmailParseResult =
  | { ok: true; normalized: string }
  | { ok: false; reason: ContactEmailRejection };

/**
 * Validate a candidate delivery address.
 *
 * Shape-only on purpose: the address is proved by a code actually arriving in
 * the inbox, so a stricter regex would only reject valid-but-unusual addresses
 * without adding any security. The one hard rule is that a `.invalid` address
 * can never become a contact email — that would put a synthetic auth identifier
 * back on the send path we just separated it from.
 */
export function parseContactEmail(
  value: string | null | undefined,
): ContactEmailParseResult {
  const normalized = normalizeContactEmail(value);
  if (!normalized) return { ok: false, reason: "empty" };
  // RFC 5321 caps a path at 256 octets; 254 is the practical address limit.
  if (normalized.length > 254) return { ok: false, reason: "too_long" };
  if (!EMAIL_SHAPE.test(normalized)) return { ok: false, reason: "malformed" };
  if (isPlaceholderAuthEmail(normalized)) {
    return { ok: false, reason: "not_routable" };
  }
  return { ok: true, normalized };
}

export function contactEmailRejectionMessage(
  reason: ContactEmailRejection,
): string {
  switch (reason) {
    case "empty":
      return "Enter an email address.";
    case "too_long":
      return "That email address is too long.";
    default:
      return "That doesn't look like a valid email address.";
  }
}

/** The subset of a user row this module reasons about. */
export type ContactEmailFields = {
  contactEmail: string | null;
  contactEmailVerifiedAt: Date | null;
};

/**
 * The ONLY address anything is allowed to mail.
 *
 * Returns null unless the address is present and verified. An unverified
 * candidate is never a destination: a typo landing on a real stranger's inbox
 * would hand them a scannable ticket, which is gate-crashing and a privacy
 * breach, not merely a bounce.
 */
export function resolveDeliverableContactEmail(
  row: ContactEmailFields | null | undefined,
): string | null {
  if (!row?.contactEmail || !row.contactEmailVerifiedAt) return null;
  const normalized = normalizeContactEmail(row.contactEmail);
  if (!normalized || isPlaceholderAuthEmail(normalized)) return null;
  return normalized;
}

/**
 * The address a TRANSACTIONAL message may go to, verified or not.
 *
 * Same normalisation and the same refusal to touch a `.invalid` placeholder —
 * those are correctness, not policy. The one thing it drops is the
 * `contactEmailVerifiedAt` requirement.
 *
 * ## Why transactional mail is exempt
 *
 * Verification earns the right to mail someone *unsolicited*: a campaign, a
 * nudge, anything they did not ask for and cannot opt out of in the moment. A
 * wrong address there is spam, and it is the reason
 * `resolveDeliverableContactEmail` exists.
 *
 * A paid ticket is not unsolicited. The person typed the address, paid for the
 * seat, and is asking for the one message that tells them where to turn up.
 * Requiring a round-trip code before we answer that question made the product
 * strictly harder to use and bought nothing: the only thing it prevented was a
 * mistyped address reaching a stranger, and the QR only unlocks a seat the
 * buyer already paid for and the venue already knows the name of.
 *
 * The cost when it does go wrong is a stranger deleting one email. The cost of
 * gating it is a confirmed attendee standing at the door with no ticket, no
 * notification and no way to recover — which is the failure this whole outbox
 * was built to eliminate.
 *
 * Keep using `resolveDeliverableContactEmail` everywhere a message is
 * broadcast rather than requested.
 */
export function resolveTransactionalContactEmail(
  row: ContactEmailFields | null | undefined,
): string | null {
  if (!row?.contactEmail) return null;
  const normalized = normalizeContactEmail(row.contactEmail);
  if (!normalized || isPlaceholderAuthEmail(normalized)) return null;
  return normalized;
}

/**
 * Can a ticket email actually leave for this event seat?
 *
 * A guest seat mails `guestEmail`. An account seat mails whatever address the
 * account holds. Both resolve through `resolveTransactionalContactEmail`, so
 * the question is only "is there a real mailbox" — verification is not required,
 * matching the send path — and a `.invalid` placeholder never counts.
 *
 * ## Why this is not `Boolean(guestEmail) || Boolean(attendeeUserId)`
 *
 * That expression asks whether the seat has an *identity*, not whether it has
 * an *address*, and the commonest signup here is a phone OTP — an account with
 * no contact email at all. Such a member could book a paid seat for themselves
 * and be handed a "Send ticket email" button that had nowhere to send.
 *
 * Worse, the click did not visibly fail. `createTransactionalDelivery` records a
 * row with no destination as `skipped` / `missing_contact_point` and returns
 * `ok: true` with `state: "pending_contact"`, because for the automatic path
 * that genuinely is resumable. The venue saw a green "Ticket email sent again."
 * over a message that was never delivered, and the burn was invisible until the
 * door list later disagreed with the toast.
 */
export function canDeliverTicketEmailFor(seat: {
  guestEmail: string | null;
  attendeeUserId: string | null;
  attendeeEmail: string | null;
}): boolean {
  if (
    resolveTransactionalContactEmail({
      contactEmail: seat.guestEmail,
      contactEmailVerifiedAt: null,
    })
  ) {
    return true;
  }
  if (!seat.attendeeUserId) return false;
  return (
    resolveTransactionalContactEmail({
      contactEmail: seat.attendeeEmail,
      contactEmailVerifiedAt: null,
    }) !== null
  );
}

/**
 * What to show a person about their own email, or a staff member with a business
 * reason to see it. Includes an unverified address (so the UI can say "check
 * your inbox") but never a placeholder.
 */
export function resolveDisplayContactEmail(
  row: ContactEmailFields | null | undefined,
): { email: string; verified: boolean } | null {
  const normalized = normalizeContactEmail(row?.contactEmail);
  if (!normalized || isPlaceholderAuthEmail(normalized)) return null;
  return { email: normalized, verified: Boolean(row?.contactEmailVerifiedAt) };
}

/**
 * Classify an address's provenance for `contactEmailSource`. Apple's relay is
 * called out because it is a real, deliverable mailbox that nonetheless needs
 * the sending domain registered with Apple before SES can reach it at all — so
 * "why did this one address bounce for everyone" has to be answerable.
 */
export function classifyContactEmailSource(
  normalized: string,
  fromProvider: boolean,
): "oauth" | "user" | "apple_relay" {
  if (isApplePrivateRelayEmail(normalized)) return "apple_relay";
  return fromProvider ? "oauth" : "user";
}

/** Neutral fallback for a person whose name we do not have. */
export const CONTACT_GREETING_FALLBACK = "there";

/**
 * First name for email greetings. Returns the neutral fallback rather than the
 * temporary auth identity, so no message ever opens with "Hi Hiive member," or,
 * worse, "Hi +919876543210,".
 */
export function contactGreetingName(
  name: string | null | undefined,
  temporaryNames: readonly string[] = [],
): string {
  const trimmed = name?.trim();
  if (!trimmed) return CONTACT_GREETING_FALLBACK;
  if (temporaryNames.includes(trimmed)) return CONTACT_GREETING_FALLBACK;
  // A phone number is never a greeting, whatever column it arrived in. Shares
  // the predicate with the display-name rules so the two can never disagree
  // about what counts as phone-shaped.
  if (looksLikePhoneNumber(trimmed)) return CONTACT_GREETING_FALLBACK;
  const first = trimmed.split(/\s+/)[0];
  return first && first.length > 0 ? first : CONTACT_GREETING_FALLBACK;
}
